/**
 * SceneManager — wraps Phaser scene transitions with push/pop stack.
 *
 * Stored in game.registry so all scenes can navigate.
 *
 * - goto(key, data?) — replace current scene
 * - push(key, data?) — enter scene, remember return point
 * - pop(result?) — return to previous scene with result data
 */

declare const Phaser: any;

export type TransitionType = "fade" | "slide" | "instant";

interface StackEntry {
  key: string;
  data?: any;
}

export class SceneManager {
  private game: any;
  private stack: StackEntry[] = [];
  private _currentKey: string | null = null;

  constructor(game: any, initialSceneKey?: string) {
    this.game = game;
    this._currentKey = initialSceneKey ?? null;
  }

  get currentKey(): string | null {
    return this._currentKey;
  }

  /**
   * Replace current scene entirely (clears stack).
   */
  goto(key: string, data?: any, transition: TransitionType = "instant"): void {
    this.stack = [];
    this.startScene(key, data, transition);
  }

  /**
   * Push a new scene onto the stack. The current scene is paused/stopped.
   */
  push(key: string, data?: any, transition: TransitionType = "instant"): void {
    if (this._currentKey) {
      this.stack.push({ key: this._currentKey });
    }
    this.startScene(key, data, transition);
  }

  /**
   * Pop back to the previous scene, passing result data.
   */
  pop(result?: any, transition: TransitionType = "instant"): void {
    const prev = this.stack.pop();
    if (!prev) {
      console.warn("SceneManager: nothing to pop to");
      return;
    }
    const returnData = { _returnResult: result, ...(prev.data || {}) };
    this.startScene(prev.key, returnData, transition);
  }

  private startScene(key: string, data?: any, transition: TransitionType = "instant"): void {
    const prevKey = this._currentKey;
    this._currentKey = key;

    if (transition === "instant" || !prevKey) {
      if (prevKey) {
        this.game.scene.stop(prevKey);
      }
      this.game.scene.start(key, data);
    } else if (transition === "fade") {
      const currentScene = this.game.scene.getScene(prevKey);
      if (currentScene) {
        currentScene.cameras.main.fadeOut(200, 0, 0, 0);
        currentScene.cameras.main.once("camerafadeoutcomplete", () => {
          this.game.scene.stop(prevKey);
          this.game.scene.start(key, data);
          const nextScene = this.game.scene.getScene(key);
          if (nextScene && nextScene.cameras && nextScene.cameras.main) {
            nextScene.cameras.main.fadeIn(200, 0, 0, 0);
          }
        });
      } else {
        this.game.scene.start(key, data);
      }
    } else if (transition === "slide") {
      // Slide falls back to fade for now
      this.startScene(key, data, "fade");
    }
  }
}
